import { parse } from 'query-string';
import { useQuery, useQueryClient } from 'react-query';
import { FailedLouiseQuote, LouiseQuote } from '../../../../../../types/Quotes';
import { wgApi } from '../../../../../app/src/features/api/wgApi';
import { isStaging } from '../../../../../common/src/utils/globalVariables';
import { QuoteSpecifications } from '../../../store/redux/slices/offersSlice';
import mockedVillasureQuote from '../misc/mockedVillasureQuote.json';

interface Params {
  product_id: string;
  quoteUrl: string;
  selectedGuarantees: string[];
  sessionId: string;
  quoteSpecs?: QuoteSpecifications;
  lang?: string;
}

const retrieveQuote = async ({
  quoteUrl,
  sessionId,
  selectedGuarantees,
  lang,
  quoteSpecs = {
    save_quote: true,
  },
}: Params) => {
  const url = new URL(quoteUrl);
  const parsedParams = parse(url.search);

  // Remove 'option' parameters because we manually set them with selectedGuarantees param
  for (const key in parsedParams) {
    if (key === 'option') {
      delete parsedParams[key];
    }
  }

  const path = quoteUrl.split('?')[0];
  const { data } = await wgApi.post<LouiseQuote | FailedLouiseQuote>(
    path,
    {
      id: sessionId,
      quote_specifications: { save_quote: true, ...quoteSpecs },
    },
    {
      query: {
        ...parsedParams,
        option: selectedGuarantees,
        lang,
      },
    },
  );

  /** Because Villasure staging api does not work, we mock a quote repsonse from production */
  if (quoteUrl.includes('villasure') && isStaging) {
    return mockedVillasureQuote as unknown as LouiseQuote;
  }
  return data;
};

const getUseQuoteBaseQKey = (productId: string) => [
  'sales-conversation',
  'quotes',
  productId,
];

const getUseQuoteQKey = (params: Params) => [
  ...getUseQuoteBaseQKey(params.product_id),
  params,
];

export const useQuoteCache = (productId: string) => {
  const qKey = getUseQuoteBaseQKey(productId);
  const queryClient = useQueryClient();
  const fetchingCount = queryClient.isFetching({
    queryKey: qKey,
    exact: false,
  });
  const data = queryClient.getQueryData<LouiseQuote>(qKey, {
    exact: false,
  });
  return {
    isFetching: fetchingCount > 0,
    data,
  };
};

export const useQuote = (params: Params) => {
  return useQuery<LouiseQuote | FailedLouiseQuote>(
    getUseQuoteQKey(params),
    () => retrieveQuote(params),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: !!params.quoteUrl,
    },
  );
};
